import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useCart } from "../lib/cart";
import { motion } from "framer-motion";

export function Header({ dark = false }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const cart = useCart();
  const nav = useNavigate();
  const fg = dark ? "text-paper" : "text-ink";
  const bg = dark ? "bg-ink/70" : "bg-paper/80";
  const border = dark ? "border-white/15" : "border-black/10";

  const businessName = settings?.business_name || "GTA Wholesale Supply";
  const canOrder = settings?.ordering_enabled && user && user.role === "retailer" && user.status === "approved";
  const cartUnits = cart?.totals?.units || 0;

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 ${bg} backdrop-blur-xl border-b ${border}`}
      data-testid="site-header"
    >
      <div className="px-4 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className={`font-display text-xl md:text-2xl lg:text-3xl leading-none tracking-tight uppercase ${fg}`}>
          {businessName}
        </Link>
        <nav className={`hidden lg:flex items-center gap-10 text-[11px] font-bold uppercase tracking-[0.24em] ${fg}`}>
          <Link to="/catalog" className="link-underline" data-testid="nav-catalog">Catalog</Link>
          <Link to="/catalog?section=imported" className="link-underline" data-testid="nav-imported">Imported</Link>
          <Link to="/about" className="link-underline" data-testid="nav-about">About</Link>
          <Link to="/contact" className="link-underline" data-testid="nav-contact">Contact</Link>
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          {canOrder && (
            <Link to="/cart" className={`relative text-[10px] md:text-[11px] font-bold uppercase tracking-widest px-3 md:px-4 py-2 md:py-3 border ${dark ? "border-white/40 text-paper" : "border-ink text-ink"}`} data-testid="header-cart-btn">
              Cart
              {cartUnits > 0 && (
                <span className="absolute -top-2 -right-2 bg-soda text-paper text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid="cart-badge">{cartUnits}</span>
              )}
            </Link>
          )}
          {user && user.role === "admin" && (
            <>
              <Link to="/admin" className="btn-ghost text-[10px] md:text-[11px] px-3 md:px-6 py-2 md:py-3" data-testid="header-admin-btn" style={dark ? { color: "#F4F4F0", borderColor: "#F4F4F0" } : {}}>Admin</Link>
              <button onClick={() => { logout(); nav("/"); }} className="btn-primary text-[10px] md:text-[11px] px-3 md:px-6 py-2 md:py-3" data-testid="header-logout-btn">Logout</button>
            </>
          )}
          {user && user.role === "retailer" && user.status === "approved" && (
            <>
              <Link to="/portal" className={`text-[10px] md:text-[11px] font-bold uppercase tracking-widest px-3 md:px-4 py-2 md:py-3 border ${dark ? "border-white/40 text-paper" : "border-ink text-ink"}`} data-testid="header-account-btn">
                My Account
              </Link>
              <Link to="/portal/orders" className={`hidden md:inline-flex text-[10px] md:text-[11px] font-bold uppercase tracking-widest px-3 md:px-4 py-2 md:py-3 border ${dark ? "border-white/40 text-paper" : "border-ink text-ink"}`} data-testid="header-orders-btn">
                My Orders
              </Link>
              <button onClick={() => { logout(); nav("/"); }} className="btn-primary text-[10px] md:text-[11px] px-3 md:px-6 py-2 md:py-3" data-testid="header-logout-btn">Logout</button>
            </>
          )}
          {user && user.role === "retailer" && user.status !== "approved" && (
            <>
              <Link to="/portal" className={`text-[10px] md:text-[11px] font-bold uppercase tracking-widest px-3 md:px-4 py-2 md:py-3 border ${dark ? "border-white/40 text-paper" : "border-ink text-ink"}`} data-testid="header-portal-pending-btn">
                Account status
              </Link>
              <button onClick={() => { logout(); nav("/"); }} className="btn-primary text-[10px] md:text-[11px] px-3 md:px-6 py-2 md:py-3" data-testid="header-logout-btn">Logout</button>
            </>
          )}
          {!user && (
            <>
              {settings?.retailer_login_enabled && (
                <Link to="/login" className={`hidden md:inline-flex btn-ghost text-[10px] md:text-[11px] px-3 md:px-6 py-2 md:py-3`} data-testid="header-login-btn" style={dark ? { color: "#F4F4F0", borderColor: "#F4F4F0" } : {}}>Retailer login</Link>
              )}
              {settings?.enquiry_form_enabled && (
                <Link to="/enquiry" className="btn-primary text-[10px] md:text-[11px] px-3 md:px-6 py-2 md:py-3" data-testid="header-enquiry-btn">Request Pricing →</Link>
              )}
            </>
          )}
        </div>
      </div>
    </motion.header>
  );
}

export function Footer() {
  const { settings } = useSettings();
  const businessName = settings?.business_name || "GTA Wholesale Supply";
  return (
    <footer className="bg-ink text-paper border-t border-white/10" data-testid="site-footer">
      <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] uppercase">
            {businessName}
          </div>
          <p className="mt-6 max-w-md text-sm text-white/70 leading-relaxed">
            {settings?.footer_text || "Independent Canadian wholesale distributor serving the Greater Toronto Area and surrounding Ontario."}
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="overline text-white/60 mb-4">Contact</div>
          <div className="text-sm space-y-2">
            {settings?.show_phone && settings?.phone && <div data-testid="footer-phone">{settings.phone}</div>}
            {settings?.show_email && settings?.public_email && <div data-testid="footer-email">{settings.public_email}</div>}
            {settings?.hours && <div className="text-white/70">{settings.hours}</div>}
            {settings?.address && <div className="text-white/70">{settings.address}</div>}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="overline text-white/60 mb-4">Explore</div>
          <div className="text-sm space-y-2">
            <Link to="/catalog" className="block link-underline">Catalog</Link>
            <Link to="/about" className="block link-underline">About</Link>
            <Link to="/contact" className="block link-underline">Contact</Link>
            {settings?.enquiry_form_enabled && <Link to="/enquiry" className="block link-underline">Request pricing</Link>}
            {settings?.retailer_login_enabled && <Link to="/login" className="block link-underline">Retailer login</Link>}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="overline text-white/60 mb-4">Follow</div>
          <div className="text-sm space-y-2 text-white/70">
            {settings?.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="block link-underline">Facebook</a>}
            {settings?.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="block link-underline">Instagram</a>}
            {settings?.linkedin_url && <a href={settings.linkedin_url} target="_blank" rel="noreferrer" className="block link-underline">LinkedIn</a>}
            {!(settings?.facebook_url || settings?.instagram_url || settings?.linkedin_url) && (
              <Link to="/catalog?coming=1" className="block link-underline" data-testid="footer-coming-soon">Coming soon →</Link>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-white/50">
        <div>© {new Date().getFullYear()} {businessName}. All rights reserved.</div>
        <div className="mt-2 md:mt-0">Ontario, Canada</div>
      </div>
    </footer>
  );
}
